import React, {useEffect, useRef, useState} from 'react';
import {ServiceResource, useAuth} from '../hooks/use-auth';
import {Notification} from '../navigation/page-layout';
import {CommonBridgeDataRequest, useInboundBridgeFilterData} from '../hooks/use-inbound-bridge-filter-data';
import {translateErrorToReactNode} from '../common';
import {Box, Container, ContentLayout, Header, SpaceBetween, Spinner, Tabs} from '@amzn/awsui-components-react';
import {BridgeType, InboundBridgeChannelPlanActual} from './index';
import {InboundBridgeFilters} from './inbound-bridge-filters';
import {useRetrieveInboundBridgePreset} from '../hooks/use-analytics-api';
import '../../assets/styles/inbound-bridging-styles.scss';
import {InboundBridgeChannelPlanActualTable} from './inbound-bridge-channel-plan-actual-table';
import {InboundBridgeChannelPlanActualByNodeTable} from './inbound-bridge-channel-plan-actual-by-node-table';

const COMPONENT_TEXTS = {
    page_title: 'Channel Breakdown',
    page_description: 'Plan to plan bridging by channel',
} as const;

export function InboundBridgeP2PChannels(props: {pushNotification: (notification: Notification) => void}) {
    function createErrorListener(header: string) {
        return (e: unknown) => {
            props.pushNotification({
                type: 'error',
                content: translateErrorToReactNode(e),
                header,
            });
        };
    }

    const auth = useAuth();
    const [channelBridgeDataDaily, setChannelBridgeDataDaily] = useState<InboundBridgeChannelPlanActual[]>([]);
    const isLoadingDaily = useRef<boolean>(true);

    const tenant = {
        business: auth.authInformation!.current!.businessId,
        country: auth.authInformation!.current!.country,
    };

    const clientConfiguration = auth.authInformation!.getCurrentServiceEndpoint(ServiceResource.ExplainabilityView);

    const {
        execute: executeRetrieveDaily,
        status: retrieveDailyStatus,
        value: retrieveDailyResponse,
    } = useRetrieveInboundBridgePreset(clientConfiguration, createErrorListener('RetrieveDailyChannelFromToPlan failed'), [auth]);

    const {nodeFilterDataMap, selectedFilter, setSelectedFilter, isLoadingFilterData} = useInboundBridgeFilterData(
        BridgeType.P2P,
        tenant,
        false,
        clientConfiguration,
        createErrorListener,
        (params: CommonBridgeDataRequest) => {
            executeRetrieveDaily({
                ...tenant,
                forecastType: 'sop',
                bridgeType: BridgeType.P2P,
                timeGranularity: 'DAY',
                nodeGranularity: 'NODE',
                preset: 'CHANNEL_FROM_TO_PLAN',
                filter: JSON.stringify({
                    nodes: params.nodes,
                    balanceDateStart: params.balanceDateStart,
                    balanceDateEnd: params.balanceDateEnd,
                    fromHorizonInWeeks: params.fromHorizonInWeeks,
                    toHorizonInWeeks: params.toHorizonInWeeks,
                }),
            });
        }
    );

    isLoadingDaily.current = ['idle', 'pending'].includes(retrieveDailyStatus);

    useEffect(() => {
        if (!isLoadingDaily.current && retrieveDailyResponse?.records) {
            setChannelBridgeDataDaily(
                retrieveDailyResponse.records.map(
                    (record) =>
                        ({
                            node: record.node,
                            date: record.balanceDate,
                            channel: record.granularity['channel'],
                            name: record.name,
                            value: record.value,
                        } as InboundBridgeChannelPlanActual)
                )
            );
        }
    }, [isLoadingDaily.current]);

    return (
        <ContentLayout defaultPadding>
            <SpaceBetween direction="vertical" size="xxl">
                <Header variant="h2" description={COMPONENT_TEXTS.page_description}>
                    {COMPONENT_TEXTS.page_title}
                </Header>
                <InboundBridgeFilters
                    bridgeType={BridgeType.P2P}
                    tenant={tenant}
                    nodeSelectConfig={{hideNodeSelect: false, multiSelectNode: true, useCoastSelect: true}}
                    nodeFilterDataMap={nodeFilterDataMap}
                    selectedFilter={selectedFilter}
                    setSelectedFilter={setSelectedFilter}
                    isLoadingFilterData={isLoadingFilterData}
                    isLoadingData={isLoadingDaily.current}
                    onApply={() => {
                        if (selectedFilter) {
                            const nodes = selectedFilter.nodes?.length ? selectedFilter.nodes : ['ALL'];
                            isLoadingDaily.current = true;
                            executeRetrieveDaily({
                                ...tenant,
                                forecastType: 'sop',
                                bridgeType: BridgeType.P2P,
                                timeGranularity: 'DAY',
                                nodeGranularity: 'NODE',
                                preset: 'CHANNEL_FROM_TO_PLAN',
                                filter: JSON.stringify({
                                    nodes,
                                    balanceDateStart: selectedFilter.balanceDateStart,
                                    balanceDateEnd: selectedFilter.balanceDateEnd,
                                    fromHorizonInWeeks: selectedFilter.fromHorizonInWeeks,
                                    toHorizonInWeeks: selectedFilter.toHorizonInWeeks,
                                    channels: selectedFilter.channels,
                                    ibTypes: selectedFilter.ibTypes,
                                    tempZones: selectedFilter.tempZones,
                                }),
                            });
                        }
                    }}
                />
                {renderVisualizations()}
            </SpaceBetween>
        </ContentLayout>
    );

    function renderVisualizations(): React.ReactElement {
        if (isLoadingFilterData) {
            return (
                <Box textAlign="center" color="inherit">
                    <Spinner size="large" />
                </Box>
            );
        }

        return (
            <Container header={<Header variant="h2">Channel Plan vs Plan</Header>}>
                <Tabs
                    tabs={[
                        {
                            id: 'aggregated',
                            label: 'Aggregated',
                            content: (
                                <InboundBridgeChannelPlanActualTable
                                    bridgeData={channelBridgeDataDaily}
                                    selectedFilter={selectedFilter}
                                    isLoading={isLoadingDaily.current}
                                    bridgeType="p2p"
                                />
                            ),
                        },
                        {
                            id: 'warehouses',
                            label: 'Warehouses',
                            content: (
                                <InboundBridgeChannelPlanActualByNodeTable
                                    bridgeData={channelBridgeDataDaily}
                                    selectedFilter={selectedFilter}
                                    isLoading={isLoadingDaily.current}
                                    bridgeType="p2p"
                                />
                            ),
                        },
                    ]}
                />
            </Container>
        );
    }
}
